import { Key, useState } from 'react';
import { ArrowDownIconSVG } from 'src/assets/svg';
import { Alert } from 'src/components/Alert';
import { Button } from 'src/components/Buttons/Button';
import Input from 'src/components/FormComponents/Input';
import { UserIcon } from 'src/components/Icons/icons';
import container from 'src/container';
import { Admin, AdminPermission } from 'src/domain/admin';
import { getFullName } from 'src/domain/user';
import { useMessageData } from 'src/view/hooks/message';
import Collapse, { Panel } from 'rc-collapse';
import { useAdmins } from 'src/view/hooks/useAdmins';
import { format, parseISO } from 'date-fns';
import nb from 'date-fns/locale/nb';
import enUS from 'date-fns/locale/en-US';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { UserActions } from 'src/state/_actions';

const {
    cradle: { munduClient },
} = container;

export const AdminList = () => {
    const { admins, mutate } = useAdmins();

    const [activeKey, setActiveKey] = useState<Key | Key[]>();
    const [deleteConfirmations, setDeleteConfirmations] = useState<
        Record<string, string>
    >({});
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const locale = i18n.language.startsWith('en') ? enUS : nb;

    const renderAdminInfo = (data: Admin) => {
        return (
            <div className="flex flex-col">
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.list.fields.name')}
                    </span>
                    {getFullName(data)}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.list.fields.phone')}
                    </span>
                    {data.phoneNumber}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.list.fields.email')}
                    </span>
                    {data.email}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.list.fields.created')}
                    </span>
                    {format(parseISO(data.createdDate), 'dd MMM yyyy', {
                        locale,
                    })}
                </p>
                <p className="flex justify-between">
                    <span className="font-semibold">
                        {t('admin.list.fields.updated')}
                    </span>
                    {data.updatedDate &&
                        format(parseISO(data.updatedDate), 'dd MMM yyyy', {
                            locale,
                        })}
                </p>
            </div>
        );
    };

    const onChange = (newActiveKey: Key | Key[]): void => {
        setActiveKey(newActiveKey);
    };

    const handleDelete = async (id: string) => {
        try {
            await munduClient.deleteAdmin(id);
            mutate();
            setMessage({
                message: t('admin.notifications.adminDeleted'),
                isSuccess: true,
            });
        } catch (e) {
            setMessage({
                message: t('admin.notifications.genericError'),
                isSuccess: false,
            });
        }
    };

    const handleSupportPermission = async (admin: Admin) => {
        const hasSupport = admin.adminPermissions?.includes(
            AdminPermission.support,
        );
        const adminPermissions = hasSupport
            ? (admin.adminPermissions ?? []).filter(
                  permission => permission !== AdminPermission.support,
              )
            : [...(admin.adminPermissions ?? []), AdminPermission.support];

        try {
            await munduClient.updateAdminPermissions(
                admin.id,
                adminPermissions,
            );
            const currentUser = await munduClient.getUser();
            dispatch(UserActions.setUser(currentUser));
            await mutate();
            setMessage({
                message: t('admin.notifications.adminPermissionsUpdated'),
                isSuccess: true,
            });
        } catch (_error) {
            setMessage({
                message: t('admin.notifications.genericError'),
                isSuccess: false,
            });
        }
    };

    return (
        <div className="overflow-y-auto rounded-lg  w-full text-woodyBrown">
            <Collapse accordion activeKey={activeKey} onChange={onChange}>
                {admins &&
                    admins.map((data, index) => (
                        <Panel
                            showArrow={false}
                            header={data.profile.firstName}
                            extra={
                                <ArrowDownIconSVG
                                    className={`h-4 w-4 transform duration-500 ${
                                        activeKey === index && 'rotate-180'
                                    }`}
                                />
                            }
                            key={index.toString()}
                        >
                            <div className="flex flex-col gap-3">
                                {renderAdminInfo(data)}
                                <div className="self-end flex items-end gap-3 flex-wrap">
                                    <Button
                                        title={t(
                                            data.adminPermissions?.includes(
                                                AdminPermission.support,
                                            )
                                                ? 'admin.list.removeSupportPermission'
                                                : 'admin.list.addSupportPermission',
                                        )}
                                        onClick={() =>
                                            handleSupportPermission(data)
                                        }
                                        active={data.adminPermissions?.includes(
                                            AdminPermission.support,
                                        )}
                                    />
                                    <Input
                                        className="text-input"
                                        type="text"
                                        onChange={e =>
                                            setDeleteConfirmations({
                                                ...deleteConfirmations,
                                                [data.id]:
                                                    e.currentTarget.value,
                                            })
                                        }
                                        label={t('common.confirmDelete', {
                                            value: data.profile.lastName,
                                        })}
                                        leadingIcon={UserIcon}
                                    />
                                    <Button
                                        title={t('admin.list.deleteButton')}
                                        onClick={() => handleDelete(data.id)}
                                        disabled={
                                            deleteConfirmations[data.id] !==
                                            data.profile.lastName
                                        }
                                    />
                                </div>
                            </div>
                        </Panel>
                    ))}
            </Collapse>
            <Alert
                isSuccess={isSuccess}
                message={message}
                clearMessage={clearMessage}
            />
        </div>
    );
};
